/**
 * bannerSurfacePolicy — the PURE half of "where may the global banner band
 * speak" (owner directive 2026-08-31: "a list of rotating banners on an
 * organization/account … needs to be consistent on each page … ALL banners
 * need to be displayed").
 *
 * Kept free of React, react-router and firebase on purpose: this is the rule
 * itself, so it must be readable and testable without a browser, an auth
 * session or a router. `bannerSurface.ts` holds the live wiring (the hook)
 * and re-exports everything here.
 *
 * WHAT THIS REPLACES. Six of the eight band sources (low-balance, trial,
 * email-verify, phone-verify, usage-limit, paused-membership) gated on
 * `isAppSurfacePath` — a 14-prefix ROUTE ALLOWLIST that did not include '/'.
 * The other two (vm-lifecycle, app-update) had no gate at all. Nobody chose
 * that split: it is simply the two sources that never got the gate the other
 * six share. The user-visible result was the reported bug — on the home page
 * only the two ungated sources could register, so the band showed ONE notice
 * and no rotation dots, while the very same account rotated two on /desktop.
 *
 * WHAT REPLACES IT — the same safety property, on the axis that carries it.
 * The allowlist existed for ONE reason, stated in its own comment: a "$0.00
 * critically low" strip must never reach a signed-out visitor on a marketing
 * page. That is a question about WHO is looking, not WHICH url they are on —
 * so this gate asks about the viewer:
 *
 *   signed in (and not anonymous)  AND  not mid-auth-flow.
 *
 * That is STRICTLY SAFER than the allowlist it replaces. The allowlist let a
 * signed-out visitor who reached an app surface register billing chrome and
 * leaned on each source's own auth check to save it — a check TWO sources
 * (TrialStatusBanner, PausedMembershipBanner) do not actually have, since
 * they gate only on hooks with signed-out fallback branches of their own.
 * This gate denies those two centrally, on every route, for the first time.
 * Signed-out visitors now see nothing anywhere; signed-in members see their
 * full rotating set everywhere. Both halves are improvements.
 *
 * WHY A DENYLIST IS RIGHT HERE (the allowlist comment argued the reverse).
 * "Fail toward hidden" was the correct instinct when the gate keyed on
 * routes: an unknown new route should not silently start showing billing
 * chrome. Keyed on the viewer, the fail-safe direction inverts — an unknown
 * route with NO signed-in user already shows nothing, because the auth half
 * fails closed. So the only thing the path half must still catch is the
 * narrow set of screens where a signed-in user is mid-identity-transition
 * and a notice about "your account" would be premature. That set is small,
 * closed, and enumerated below rather than ever-growing.
 *
 * NOT gated here: AppUpdateBanner. It is the deploy-freshness notice — about
 * the running BUILD, not about an account — so it legitimately speaks to
 * everyone on every page, signed in or not. Account-scoped notices take this
 * gate; the build-freshness notice is universal by design.
 */

/**
 * Screens where a signed-in viewer is mid-identity-transition: the account
 * the band would speak about is not yet the account they are becoming, so
 * every notice is premature. Deliberately NOT a list of "public pages" — a
 * signed-in member reading /pricing or /mission still gets their band, which
 * is the whole point of the directive.
 *
 * Matched exactly or as a path SEGMENT prefix (so '/start' never swallows
 * '/started'), case-insensitively — the allowlist it replaces was
 * case-sensitive, which silently un-gated any mixed-case link.
 */
export const BANNER_SUPPRESSED_PREFIXES = [
  '/auth',
  '/signup',
  '/start',
  '/trial',
];

/** True on the mid-auth-flow screens enumerated above. */
export function isBannerSuppressedPath(pathname: string): boolean {
  const p = (pathname || '').toLowerCase();
  return BANNER_SUPPRESSED_PREFIXES.some((s) => p === s || p.startsWith(`${s}/`));
}

/**
 * The whole predicate. `signedIn` must already exclude anonymous sessions —
 * an anonymous visitor has no account for a notice to be about.
 */
export function isBannerSurface(signedIn: boolean, pathname: string): boolean {
  return signedIn && !isBannerSuppressedPath(pathname);
}
